import {MaterialReference} from "@cm/template-nodes"
import type {NumberLike} from "@cm/template-nodes"
import type {MattressCurveLikeNode} from "#mattress/role-markers"
import type {MattressFabricLayout} from "#mattress/nodes/mattress-cut"
import type {MattressComponentGraphNode} from "#mattress/nodes/mattress-nodes"
import {createNumberInput, createNumberExport, createMaterialInput, createMathOperator} from "#mattress/assets-definitions/common"
import {
    createMattressCurveInput,
    createMattressComponentGraph,
    createMattressComponnentGraphGenerator,
    createMattressComponentInstance,
    createMattressCurveDefault,
    createMattressReferenceFrame,
    createMattressComputedCurve,
    createMattressCurveExport,
    createMattressCut,
} from "#mattress/assets-definitions/create-mattress"
import {createPath, panelPathName, stackPathName, uiPathMetaData, numberInputMetaData} from "#mattress/mattress-meta-data"
import {getComponentGraphForAccessoryStack} from "#mattress/assets-definitions/standard-models/common/accessory-stack"

export function getComponentGraphNodesForFabricStackElement(
    withAccessoryStackForLayerTopCurve: boolean = true,
    defaultMaterialLegacyId: number | undefined = undefined,
    defaultLayerHeight: number = 5,
    fabricLayout: MattressFabricLayout = "Border",
) {
    const material = createMaterialInput(
        "Material",
        "material",
        uiPathMetaData([createPath(panelPathName, 0, createPath(stackPathName, 1))], 0),
        defaultMaterialLegacyId
            ? new MaterialReference({parameters: {name: "Default Material", materialId: defaultMaterialLegacyId, materialRevisionId: null}})
            : undefined,
    )
    const bottomOffsetInput = createNumberInput("Bottom Offset", "bottom-offset", 0)
    const heightInput = createNumberInput(
        "Height",
        "height",
        defaultLayerHeight,
        numberInputMetaData([createPath(panelPathName, 3, createPath(stackPathName, 1))], 1, 100, 0),
    )
    const refFrameUnitInput = createNumberInput("Reference Frame Unit", "reference-frame-unit", 1)
    const halfHeight = createMathOperator("Half Height", "mul", heightInput, 0.5)
    const topOffset = createMathOperator("Top Offset", "add", bottomOffsetInput, heightInput)

    const topOffsetExport = createNumberExport("Top Offset", "top-offset", topOffset)

    // bottom curve
    const bottomCurveDefault = createMattressCurveDefault("Bottom Curve Default")
    const bottomCurveInput = createMattressCurveInput("Bottom Curve", "bottom-curve", bottomCurveDefault)

    // bottom reference frame
    const bottomReferenceFrame = createMattressReferenceFrame("Bottom Reference Frame", "zAxisRing", "global", bottomOffsetInput, refFrameUnitInput)

    // middle reference frame and curve
    const middleOffset = createMathOperator("Middle Offset", "add", bottomOffsetInput, halfHeight)
    const middleReferenceFrame = createMattressReferenceFrame("Middle Reference Frame", "zAxisRing", "global", middleOffset, refFrameUnitInput)
    const middleCurve = createMattressComputedCurve("Middle Curve", {
        type: "MattressComputedCurveSwapReferenceFrame",
        curve: bottomCurveInput,
        oldReferenceFrame: bottomReferenceFrame,
        newReferenceFrame: middleReferenceFrame,
    })
    const middleCurveExport = createMattressCurveExport("Middle Curve", "middle-curve", middleCurve)

    // top reference frame and curve
    const topReferenceFrame = createMattressReferenceFrame("Top Reference Frame", "zAxisRing", "global", topOffset, refFrameUnitInput)
    const topCurve = createMattressComputedCurve("Top Curve", {
        type: "MattressComputedCurveSwapReferenceFrame",
        curve: bottomCurveInput,
        oldReferenceFrame: bottomReferenceFrame,
        newReferenceFrame: topReferenceFrame,
    })
    const topCurveExport = createMattressCurveExport("Top Curve", "top-curve", topCurve)

    const cut = createMattressCut(
        "Border",
        [
            {curve: bottomCurveInput, side: "left"},
            {curve: topCurve, side: "right"},
        ],
        fabricLayout,
        material,
    )

    const accessoriesNodes: MattressComponentGraphNode[] = []
    if (withAccessoryStackForLayerTopCurve) {
        const {nodes: layerTopCurveAccessoriesNodes} = getComponentGraphForAccessoryStack(0)
        const layerTopCurveAccessoriesGraph = createMattressComponentGraph("Accessories Layer Top Curve Graph", layerTopCurveAccessoriesNodes)
        const layerTopCurveAccessories = createMattressComponentInstance(
            "Accessories Layer Top Curve",
            "accessories-layer-top-curve",
            layerTopCurveAccessoriesGraph,
            {
                "ref-curve": topCurve,
            },
        )
        accessoriesNodes.push(layerTopCurveAccessories, layerTopCurveAccessoriesGraph)
    }

    return {
        nodes: [
            material,
            bottomOffsetInput,
            heightInput,
            refFrameUnitInput,
            halfHeight,
            middleOffset,
            topOffset,
            topOffsetExport,
            bottomCurveDefault,
            bottomCurveInput,
            bottomReferenceFrame,
            middleReferenceFrame,
            middleCurve,
            middleCurveExport,
            topReferenceFrame,
            topCurve,
            topCurveExport,
            cut,
            ...accessoriesNodes,
        ],
        exports: {
            middleCurve: middleCurveExport,
            topOffset: topOffsetExport,
            topCurve: topCurveExport,
        },
    }
}

export function getComponentGraphNodesForFabricStack(
    inputData: {
        bottomOffset: NumberLike
        bottomCurve: MattressCurveLikeNode
        layerReferenceFrameUnit?: NumberLike
        layerCount: NumberLike
        defaultMaterialLegacyId?: number
        defaultLayerHeight?: number
        fabricLayout?: MattressFabricLayout
    },
    withAccessoryStackForLayerTopCurve: boolean = true,
) {
    const {bottomOffset, bottomCurve, layerCount, defaultMaterialLegacyId, defaultLayerHeight, fabricLayout, layerReferenceFrameUnit = 1} = inputData

    const fabricStackGraphBaseBottomOffsetInput = createNumberInput("Bottom Offset", "bottom-offset", 0)
    const fabricStackGraphBaseBottomCurveInput = createMattressCurveInput("Bottom Curve", "bottom-curve")
    const fabricStackGraphBaseRefFrameUnitInput = createNumberInput("Reference Frame Unit", "reference-frame-unit", 1)
    const fabricStackGraphBase = createMattressComponentGraph("Fabric Stack Base", [
        fabricStackGraphBaseBottomOffsetInput,
        fabricStackGraphBaseBottomCurveInput,
        fabricStackGraphBaseRefFrameUnitInput,
    ])

    const {nodes: fabricStackElementNodes, exports: fabricLayerStackElementExports} = getComponentGraphNodesForFabricStackElement(
        withAccessoryStackForLayerTopCurve,
        defaultMaterialLegacyId,
        defaultLayerHeight,
        fabricLayout,
    )
    const fabricStackElement = createMattressComponentGraph("Fabric Stack Element", fabricStackElementNodes)

    const fabricStackGraph = createMattressComponnentGraphGenerator("Fabric Stack", {
        type: "MattressComponentGraphGeneratorUnitRepeat1D",
        unitGraph: fabricStackElement,
        baseGraph: fabricStackGraphBase,
        unitInputs: {
            "bottom-curve": fabricLayerStackElementExports.topCurve,
            "bottom-offset": fabricLayerStackElementExports.topOffset,
            "reference-frame-unit": fabricStackGraphBaseRefFrameUnitInput,
        },
        unitIdPrefix: "layer",
        outputsIfUnitCountZero: {
            "top-offset": fabricStackGraphBaseBottomOffsetInput,
            "top-curve": fabricStackGraphBaseBottomCurveInput,
            "middle-curve": fabricStackGraphBaseBottomCurveInput,
        },
        unitCount: layerCount,
    })
    const fabricStack = createMattressComponentInstance("Fabric Stack", "fabric-stack", fabricStackGraph, {
        "layer-0/bottom-curve": bottomCurve,
        "layer-0/bottom-offset": bottomOffset,
        "bottom-curve": bottomCurve,
        "bottom-offset": bottomOffset,
        "reference-frame-unit": layerReferenceFrameUnit,
    })

    return {
        nodes: [fabricStackElement, fabricStackGraphBase, fabricStackGraph, fabricStack],
        stackInstance: fabricStack,
    }
}
